import { useState, useEffect, type ChangeEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Loader2,
  ArrowLeft,
  CheckCircle,
  Mail,
  UserCheck,
  Clock,
  AlertTriangle,
  Shield,
  User,
  Key,
  CupSoda,
  Camera,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { storage } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const favoriteDrinkValues = ['coke', 'pepsi', 'black_coffee', 'latte', 'tea', 'sparkling_water', 'other'] as const;

type FavoriteDrinkOption = (typeof favoriteDrinkValues)[number];

const FAVORITE_DRINK_OPTIONS: Array<{ value: FavoriteDrinkOption; label: string }> = [
  { value: 'coke', label: 'Coke' },
  { value: 'pepsi', label: 'Pepsi' },
  { value: 'black_coffee', label: 'Black coffee' },
  { value: 'latte', label: 'Latte' },
  { value: 'tea', label: 'Tea' },
  { value: 'sparkling_water', label: 'Sparkling water' },
  { value: 'other', label: 'Other' },
];

const isFavoriteDrinkOption = (value: unknown): value is FavoriteDrinkOption =>
  typeof value === 'string' && (favoriteDrinkValues as readonly string[]).includes(value);

const NONE_OPTION_VALUE = '__none__';

const MAX_PROFILE_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  displayName: z.string().min(2, 'Display name must be at least 2 characters'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string().min(6, 'Please confirm your password'),
  favoriteDrink: z.enum(favoriteDrinkValues).optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

// Role display mapping
const getRoleDisplay = (role: string) => {
  switch (role) {
    case 'bevpass_admin':
      return { label: 'BevPass Admin', color: 'bg-purple-100 text-purple-800', description: 'Full platform access' };
    case 'merchant_owner':
      return { label: 'Merchant Owner', color: 'bg-blue-100 text-blue-800', description: 'Manage assigned merchants' };
    case 'merchant_viewer':
      return { label: 'Merchant Viewer', color: 'bg-green-100 text-green-800', description: 'View assigned merchants' };
    default:
      return { label: role, color: 'bg-gray-100 text-gray-800', description: 'Custom role' };
  }
};

type UserExistsState = {
  exists: boolean;
  isPreApproved: boolean;
  displayName?: string;
  message?: string;
  role?: string;
  roleDisplayName?: string;
  merchantIds?: string[];
  status?: string;
  requiresActivation?: boolean;
  userId?: string;
  favoriteDrink?: string | null;
  photoURL?: string | null;
};

export function RegisterPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [userExistsState, setUserExistsState] = useState<UserExistsState | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      favoriteDrink: undefined,
    },
  });

  const watchedEmail = watch('email');

  const handleProfileImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      setProfileImageFile(null);
      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_PROFILE_IMAGE_SIZE_BYTES) {
      setError('Profile photo must be 5MB or smaller.');
      setProfileImageFile(null);
      return;
    }

    setError(null);
    setProfileImageFile(file);
  };

  // Check if user exists when email changes
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkUserExists = async (email: string) => {
      if (!email || !email.includes('@')) {
        setUserExistsState(null);
        return;
      }

      setIsCheckingEmail(true);
      setError(null);

      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const checkUserExistsFn = httpsCallable(functions, 'checkUserExists');

        const result = await checkUserExistsFn({ email });
        const data = result.data as UserExistsState;
        setUserExistsState(data);
        setProfileImageFile(null);

        if (data.exists && data.displayName) {
          setValue('displayName', data.displayName, { shouldValidate: false, shouldDirty: false });
        }

        if (data.exists && isFavoriteDrinkOption(data.favoriteDrink)) {
          setValue('favoriteDrink', data.favoriteDrink, { shouldValidate: false, shouldDirty: false });
        } else {
          setValue('favoriteDrink', undefined, { shouldValidate: false, shouldDirty: false });
        }
      } catch (error: unknown) {
        console.error('Error checking user existence:', error);
        setUserExistsState(null);
      } finally {
        setIsCheckingEmail(false);
      }
    };

    if (watchedEmail) {
      // Debounce the check
      timeoutId = setTimeout(() => {
        checkUserExists(watchedEmail);
      }, 500);
    } else {
      setUserExistsState(null);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [watchedEmail]);

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();

      // If user exists and is pre-approved, use simplified setup
      if (userExistsState?.exists && userExistsState.isPreApproved) {
        const setupUserAccount = httpsCallable(functions, 'setupUserAccount');

        let uploadedPhotoURL: string | null = null;

        if (profileImageFile) {
          if (!userExistsState?.userId) {
            setError('Unable to upload profile photo. Missing user reference. Please contact support.');
            setIsLoading(false);
            return;
          }

          try {
            const extension = profileImageFile.name.split('.').pop()?.toLowerCase() || 'jpg';
            const storagePath = `users/${userExistsState.userId}/profile.${extension}`;
            const storageRef = ref(storage, storagePath);

            await uploadBytes(storageRef, profileImageFile, {
              contentType: profileImageFile.type || 'image/jpeg',
            });

            uploadedPhotoURL = await getDownloadURL(storageRef);
          } catch (uploadError) {
            console.error('Profile photo upload failed:', uploadError);
            setError('Failed to upload profile photo. Please try again.');
            setIsLoading(false);
            return;
          }
        }

        const result = await setupUserAccount({
          email: data.email,
          displayName: data.displayName,
          password: data.password,
          favoriteDrink: data.favoriteDrink ?? null,
          photoURL: uploadedPhotoURL ?? userExistsState?.photoURL ?? null,
        });

        console.log('Account setup result:', result.data);
        setIsSubmitted(true);
      } else {
        // Show error for users not found or not pre-approved
        setError('No pre-approved account found for this email address. Please contact your administrator to request access.');
      }
    } catch (error: unknown) {
      console.error('Account setup error:', error);
      setError(error instanceof Error ? error.message : 'Failed to setup account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold">
              Account Activated!
            </CardTitle>
            <CardDescription>
              Your account has been successfully activated. You can now sign in to access your dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">You're all set!</h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Your password has been set successfully</li>
                <li>• Your role and permissions are now active</li>
                <li>• You can sign in immediately</li>
                <li>• Access your dashboard with full functionality</li>
              </ul>
            </div>
            
            <div className="text-center pt-4">
              <Button
                onClick={() => navigate('/auth/login')}
                className="w-full"
              >
                Sign In Now
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <Link
              to="/auth/login"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login
            </Link>
          </div>
          <CardTitle className="text-2xl font-bold">
            Activate Your Account
          </CardTitle>
          <CardDescription>
            Set up your pre-approved BevPass admin account
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email">
                <Mail className="inline-block w-4 h-4 mr-2" />
                Email Address *
              </Label>
              <div className="relative">
                <Input
                  {...register('email')}
                  type="email"
                  id="email"
                  placeholder="admin@yourbusiness.com"
                  disabled={isLoading}
                  autoComplete="email"
                />
                {isCheckingEmail && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* User Status Alert */}
            {userExistsState?.exists && (
              <div className="mt-2">
                {userExistsState.isPreApproved && userExistsState.requiresActivation ? (
                  <Alert className="border-green-200 bg-green-50">
                    <UserCheck className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      <strong>Account found!</strong> Pre-approved {getRoleDisplay(userExistsState.role || '').label} account ready for activation.
                    </AlertDescription>
                  </Alert>
                ) : userExistsState.status === 'pending_approval' ? (
                  <Alert className="border-amber-200 bg-amber-50">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      You already have a pending registration request. Please wait for admin approval.
                    </AlertDescription>
                  </Alert>
                ) : userExistsState.status === 'rejected' ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Your previous registration request was rejected. Please contact the administrator.
                    </AlertDescription>
                  </Alert>
                ) : userExistsState.status === 'active' ? (
                  <Alert className="border-blue-200 bg-blue-50">
                    <UserCheck className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      You already have an active account. Please use the login page instead.
                    </AlertDescription>
                  </Alert>
                ) : null}
              </div>
            )}

            {/* Only show remaining fields if user is found and pre-approved */}
            {userExistsState?.exists && userExistsState.isPreApproved && (
              <>
                {/* Role Display */}
                <div className="space-y-2">
                  <Label>
                    <Shield className="inline-block w-4 h-4 mr-2" />
                    Assigned Role
                  </Label>
                  <div className="p-3 hairline rounded-md bg-gray-50">
                    <div className="flex items-center gap-2">
                      <Badge className={getRoleDisplay(userExistsState.role || '').color}>
                        {getRoleDisplay(userExistsState.role || '').label}
                      </Badge>
                      <span className="text-sm text-gray-600">
                        {getRoleDisplay(userExistsState.role || '').description}
                      </span>
                    </div>
                    {userExistsState.merchantIds && userExistsState.merchantIds.length > 0 && (
                      <div className="mt-2 text-sm text-gray-600">
                        <strong>Merchants:</strong> {userExistsState.merchantIds.length} assigned
                      </div>
                    )}
                  </div>
                </div>

                {/* Display Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="displayName">
                    <User className="inline-block w-4 h-4 mr-2" />
                    Display Name *
                  </Label>
                  <Input
                    {...register('displayName')}
                    type="text"
                    id="displayName"
                    placeholder="John Smith"
                    disabled={isLoading}
                    autoComplete="name"
                  />
                  {errors.displayName && (
                    <p className="text-sm text-red-600">{errors.displayName.message}</p>
                  )}
                </div>

                {/* Password Fields */}
                <div className="space-y-2">
                  <Label htmlFor="password">
                    <Key className="inline-block w-4 h-4 mr-2" />
                    Password *
                  </Label>
                  <Input
                    {...register('password')}
                    type="password"
                    id="password"
                    placeholder="Enter your password"
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                  {errors.password && (
                    <p className="text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    <Key className="inline-block w-4 h-4 mr-2" />
                    Confirm Password *
                  </Label>
                  <Input
                    {...register('confirmPassword')}
                    type="password"
                    id="confirmPassword"
                    placeholder="Confirm your password"
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-600">{errors.confirmPassword.message}</p>
                  )}
                </div>

                {/* Favorite Drink Field */}
                <div className="space-y-2">
                  <Label htmlFor="favoriteDrink">
                    <CupSoda className="inline-block w-4 h-4 mr-2" />
                    Favorite Drink (optional)
                  </Label>
                  <Controller
                    control={control}
                    name="favoriteDrink"
                    render={({ field }) => (
                      <Select
                        value={field.value ?? NONE_OPTION_VALUE}
                        onValueChange={(value) => {
                          if (value === NONE_OPTION_VALUE) {
                            field.onChange(undefined);
                          } else {
                            field.onChange(value as FavoriteDrinkOption);
                          }
                        }}
                        disabled={isLoading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a drink (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE_OPTION_VALUE}>No preference</SelectItem>
                          {FAVORITE_DRINK_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <p className="text-sm text-muted-foreground">
                    We use this to personalize onboarding. You can change it later.
                  </p>
                </div>

                {/* Profile Photo Field */}
                <div className="space-y-2">
                  <Label htmlFor="profilePhoto">
                    <Camera className="inline-block w-4 h-4 mr-2" />
                    Profile Photo (optional)
                  </Label>
                  <Input
                    id="profilePhoto"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={isLoading}
                    onChange={handleProfileImageChange}
                  />
                  {profileImageFile ? (
                    <p className="text-sm text-muted-foreground">
                      Selected file: {profileImageFile.name}
                    </p>
                  ) : userExistsState?.photoURL ? (
                    <p className="text-sm text-muted-foreground">
                      We have a profile photo on file. Uploading a new image will replace it.
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Optional. JPG, PNG, or WEBP up to 5MB.
                    </p>
                  )}
                </div>
              </>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Submit Button - Only show if user is pre-approved */}
            {userExistsState?.exists && userExistsState.isPreApproved && (
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting up account...
                  </>
                ) : (
                  'Activate Account & Set Password'
                )}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default RegisterPage;
